import { useState } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { exhibitionService, tagService } from '../../services';
import { LoadingSpinner, ConfirmDialog } from '../ui';
import { useToast, ToastProvider } from '../ui/Toast';
import { FeaturedExhibitionLimitDialog } from './FeaturedExhibitionLimitDialog';
import {
  AddButton,
  CloseButton,
  EmptyState,
  ErrorMessage,
  Form,
  ManagementContainer,
  Modal,
  ModalActions,
  ModalButton,
  ModalContent,
  ModalHeader,
  ModalTitle,
  Section,
  SectionHeader,
  SectionTitle,
} from './shared/AdminPrimitives';

const FeaturedCounter = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray.dark};
  background: ${({ theme }) => theme.colors.gray.light};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.full};
`;

const CounterIcon = styled.div`
  font-size: 16px;
`;

const CounterText = styled.span<{ $isNearLimit?: boolean }>`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme, $isNearLimit }) => 
    $isNearLimit ? theme.colors.error : theme.colors.primary};
`;

const ExhibitionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
`;

const ExhibitionCard = styled.div<{ $isFeatured?: boolean }>`
  background: ${({ theme, $isFeatured }) => 
    $isFeatured 
      ? `linear-gradient(135deg, ${theme.colors.primary}10 0%, ${theme.colors.secondary}10 100%)`
      : theme.colors.background
  };
  border: 2px solid ${({ theme, $isFeatured }) => 
    $isFeatured ? theme.colors.primary : theme.colors.gray.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  transition: all ${({ theme }) => theme.transitions.normal};
  position: relative;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.md};
    transform: translateY(-2px);
  }
`;

const FeaturedBadge = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing.md};
  right: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
`;

const ExhibitionHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ExhibitionTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
`;

const ExhibitionMeta = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray.dark};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ExhibitionDescription = styled.p`
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const TagChip = styled.span`
  background: ${({ theme }) => theme.colors.primary}20;
  color: ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const ExhibitionStats = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const StatLabel = styled.div`
  color: ${({ theme }) => theme.colors.gray.dark};
`;

const ExhibitionActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' | 'feature' | 'unfeature' }>`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.sm};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  position: relative;
  z-index: 1;
  
  /* Ensure clickability */
  pointer-events: auto;
  user-select: none;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    pointer-events: none;
  }
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }

  ${({ $variant = 'primary', theme }) => {
    switch ($variant) {
      case 'secondary':
        return `
          background-color: ${theme.colors.gray.medium};
          color: ${theme.colors.white};
          
          &:hover {
            background-color: ${theme.colors.gray.dark};
          }
        `;
      case 'danger':
        return `
          background-color: ${theme.colors.error};
          color: ${theme.colors.white};
          
          &:hover {
            background-color: ${theme.colors.error}dd;
          }
        `;
      case 'feature':
        return `
          background-color: #f59e0b;
          color: ${theme.colors.white};
          
          &:hover {
            background-color: #d97706;
          }
        `;
      case 'unfeature':
        return `
          background-color: #10b981;
          color: ${theme.colors.white};
          
          &:hover {
            background-color: #059669;
          }
        `;
      default:
        return `
          background-color: ${theme.colors.primary};
          color: ${theme.colors.white};
          
          &:hover {
            background-color: ${theme.colors.secondary};
          }
        `;
    }
  }}
`;

const ExhibitionModalContent = styled(ModalContent)`
  max-width: 600px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const Input = styled.input<{ $hasError?: boolean }>`
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme, $hasError }) => 
    $hasError ? theme.colors.error : theme.colors.gray.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${({ theme, $hasError }) => 
      $hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme, $hasError }) => 
      $hasError ? theme.colors.error : theme.colors.primary}20;
  }
`;

const TextArea = styled.textarea<{ $hasError?: boolean }>`
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme, $hasError }) => 
    $hasError ? theme.colors.error : theme.colors.gray.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${({ theme, $hasError }) => 
      $hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme, $hasError }) => 
      $hasError ? theme.colors.error : theme.colors.primary}20;
  }
`;

const TagSelector = styled.div`
  border: 2px solid ${({ theme }) => theme.colors.gray.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  max-height: 200px;
  overflow-y: auto;
`;

const TagOption = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.xs} 0;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colors.gray.light};
  }
`;

const Checkbox = styled.input`
  margin: 0;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const schema = yup.object({
  title: yup.string().required('Exhibition title is required'),
  description: yup.string().optional(),
  tags: yup.array().of(yup.string().defined()).min(1, 'Please select at least one tag'),
});

interface ExhibitionFormData {
  title: string;
  description?: string;
  tags?: string[];
}

export function ExhibitionManagement() {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingExhibition, setEditingExhibition] = useState<any>(null);
  const [deleteExhibition, setDeleteExhibition] = useState<any>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isLimitDialogOpen, setIsLimitDialogOpen] = useState(false);
  const [featuredExhibitions, setFeaturedExhibitions] = useState<any[]>([]);
  const [pendingFeaturedToggle, setPendingFeaturedToggle] = useState<number | null>(null);
  const toast = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<ExhibitionFormData>({
    resolver: yupResolver(schema),
  });

  // Fetch exhibitions
  const { data: exhibitionsData, isLoading } = useQuery({
    queryKey: ['admin', 'exhibitions'],
    queryFn: () => exhibitionService.getAdminExhibitions(1, 100),
  });

  // Fetch available tags
  const { data: availableTagsData } = useQuery({
    queryKey: ['admin', 'tags'],
    queryFn: () => tagService.getAdminTags({ includeUsageCount: true }),
  });
  const availableTags = availableTagsData?.tags || [];

  // Fetch featured exhibitions
  const { data: featuredExhibitionsData } = useQuery({
    queryKey: ['admin', 'featured-exhibitions'],
    queryFn: () => exhibitionService.getAdminFeaturedExhibitions(),
  });
  const currentFeaturedExhibitions = featuredExhibitionsData || [];

  // Create exhibition mutation
  const createMutation = useMutation({
    mutationFn: (data: ExhibitionFormData) => {
      const tagIds = availableTags?.filter((tag: any) => data.tags?.includes(tag.tag_name)).map((tag: any) => tag.tag_id) || [];
      return exhibitionService.createExhibition({
        title: data.title,
        description: data.description,
        tag_ids: tagIds,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'exhibitions'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'exhibition-stats'] });
      closeModal();
    },
  });

  // Update exhibition mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: ExhibitionFormData }) => {
      const tagIds = availableTags?.filter((tag: any) => data.tags?.includes(tag.tag_name)).map((tag: any) => tag.tag_id) || [];
      return exhibitionService.updateExhibition(id, {
        title: data.title,
        description: data.description,
        tag_ids: tagIds,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'exhibitions'] });
      closeModal();
    },
  });

  // Delete exhibition mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => exhibitionService.deleteExhibition(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'exhibitions'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'exhibition-stats'] });
      setDeleteExhibition(null);
    },
  });

  // Toggle featured mutation with limit handling
  const toggleFeaturedMutation = useMutation({
    mutationFn: async ({ id, isFeatured }: { id: number; isFeatured: boolean }) => {
      setPendingFeaturedToggle(id);
      if (isFeatured) {
        // Unfeaturing - use the unfeature endpoint
        await exhibitionService.unfeatureExhibition(id);
        return { exhibition_id: id, is_featured: false };
      } else {
        // Featuring - use the setFeatured endpoint that handles limit validation
        return await exhibitionService.setFeaturedExhibition(id);
      }
    },
    onSuccess: (_, variables) => {
      setPendingFeaturedToggle(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'exhibitions'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'featured-exhibitions'] });
      
      const action = variables.isFeatured ? 'unfeatured' : 'featured';
      toast.showSuccess(`Exhibition successfully ${action}!`);
    },
    onError: (error: any, variables) => {
      setPendingFeaturedToggle(null);
      if (error.featuredError?.code === 'FEATURED_LIMIT_EXCEEDED') {
        // Handle the featured limit error by showing the dialog
        setFeaturedExhibitions(currentFeaturedExhibitions);
        setIsLimitDialogOpen(true);
      } else {
        const action = variables.isFeatured ? 'unfeature' : 'feature';
        toast.showError(`Failed to ${action} exhibition: ${error.message || 'Unknown error'}`);
      }
    },
  });

  const openAddModal = () => {
    setEditingExhibition(null);
    setSelectedTags([]);
    reset({ title: '', description: '', tags: [] });
    setIsModalOpen(true);
  };

  const openEditModal = (exhibition: any) => {
    setEditingExhibition(exhibition);
    setSelectedTags(exhibition.tags || []);
    setValue('title', exhibition.title);
    setValue('description', exhibition.description || '');
    setValue('tags', exhibition.tags || []);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingExhibition(null);
    setSelectedTags([]);
    reset();
  };

  const handleTagChange = (tagName: string, checked: boolean) => {
    const newTags = checked 
      ? [...selectedTags, tagName]
      : selectedTags.filter(tag => tag !== tagName);
    
    setSelectedTags(newTags);
    setValue('tags', newTags);
  };

  const onSubmit = async (data: ExhibitionFormData) => {
    const formData = { ...data, tags: selectedTags };
    
    if (editingExhibition) {
      await updateMutation.mutateAsync({ id: editingExhibition.exhibition_id, data: formData });
    } else {
      await createMutation.mutateAsync(formData);
    }
  };

  const handleDelete = async () => {
    if (deleteExhibition) {
      await deleteMutation.mutateAsync(deleteExhibition.exhibition_id);
    }
  };

  const handleToggleFeatured = async (exhibition: any) => {
    toggleFeaturedMutation.mutate({ 
      id: exhibition.exhibition_id, 
      isFeatured: exhibition.is_featured 
    });
  };

  const handleLimitDialogSuccess = () => {
    // Refresh exhibitions and featured exhibitions after unfeaturing
    queryClient.invalidateQueries({ queryKey: ['admin', 'exhibitions'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'featured-exhibitions'] });
    toast.showSuccess('Selected exhibitions have been unfeatured!');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <>
      <ToastProvider toasts={toast.toasts} onRemove={toast.removeToast} />
      <ManagementContainer>
        <Section>
          <SectionHeader>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <SectionTitle>Exhibition Management</SectionTitle>
              <FeaturedCounter>
                <CounterIcon>✨</CounterIcon>
                <span>Featured:</span>
                <CounterText $isNearLimit={currentFeaturedExhibitions.length >= 4}>
                  {currentFeaturedExhibitions.length}/5
                </CounterText>
              </FeaturedCounter>
            </div>
            <AddButton onClick={openAddModal}>+ Create Exhibition</AddButton>
          </SectionHeader>

          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '64px 0' }}>
              <LoadingSpinner size="lg" text="Loading exhibitions..." />
            </div>
          ) : exhibitionsData?.exhibitions?.length === 0 ? (
            <EmptyState>
              <EmptyIcon>🏛️</EmptyIcon>
              <h3>No Exhibitions Yet</h3>
              <p>
                Create your first exhibition to showcase curated collections of student artwork.
              </p>
            </EmptyState>
          ) : (
            <ExhibitionsGrid>
              {exhibitionsData?.exhibitions?.map((exhibition) => (
                <ExhibitionCard 
                  key={exhibition.exhibition_id}
                  $isFeatured={exhibition.is_featured}
                >
                  {exhibition.is_featured && (
                    <FeaturedBadge>✨ Featured</FeaturedBadge>
                  )}
                  
                  <ExhibitionHeader>
                    <ExhibitionTitle>{exhibition.title}</ExhibitionTitle>
                    <ExhibitionMeta>
                      Created {formatDate(exhibition.created_at)}
                    </ExhibitionMeta>
                  </ExhibitionHeader>

                  {exhibition.description && (
                    <ExhibitionDescription>
                      {exhibition.description}
                    </ExhibitionDescription>
                  )}

                  {exhibition.tags && exhibition.tags.length > 0 && (
                    <TagsContainer>
                      {exhibition.tags.slice(0, 5).map((tag) => (
                        <TagChip key={tag}>{tag}</TagChip>
                      ))}
                      {exhibition.tags.length > 5 && (
                        <TagChip>+{exhibition.tags.length - 5} more</TagChip>
                      )}
                    </TagsContainer>
                  )}

                  <ExhibitionStats>
                    <StatItem>
                      <StatValue>{exhibition.artwork_count || 0}</StatValue>
                      <StatLabel>Artworks</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue>{exhibition.tags?.length || 0}</StatValue>
                      <StatLabel>Tags</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue>{exhibition.is_featured ? 'Yes' : 'No'}</StatValue>
                      <StatLabel>Featured</StatLabel>
                    </StatItem>
                  </ExhibitionStats>

                  <ExhibitionActions>
                    <ActionButton onClick={() => openEditModal(exhibition)}>
                      Edit
                    </ActionButton>
                    <ActionButton
                      $variant={exhibition.is_featured ? 'unfeature' : 'feature'}
                      onClick={() => handleToggleFeatured(exhibition)}
                      disabled={pendingFeaturedToggle === exhibition.exhibition_id}
                    >
                      {pendingFeaturedToggle === exhibition.exhibition_id
                        ? '⏳ Updating...' 
                        : exhibition.is_featured ? '⭐ Unfeature' : '✨ Feature'
                      }
                    </ActionButton>
                    <ActionButton
                      $variant="danger"
                      onClick={() => setDeleteExhibition(exhibition)}
                    >
                      Delete
                    </ActionButton>
                  </ExhibitionActions>
                </ExhibitionCard>
              ))}
            </ExhibitionsGrid>
          )}
        </Section>
      </ManagementContainer>

      {/* Add/Edit Modal */}
      <Modal $isOpen={isModalOpen}>
        <ExhibitionModalContent>
          <ModalHeader>
            <ModalTitle>
              {editingExhibition ? 'Edit Exhibition' : 'Create New Exhibition'}
            </ModalTitle>
            <CloseButton onClick={closeModal}>×</CloseButton>
          </ModalHeader>

          <Form onSubmit={handleSubmit(onSubmit)}>
            <FormGroup>
              <Label htmlFor="title">Exhibition Title *</Label>
              <Input
                {...register('title')}
                id="title"
                type="text"
                placeholder="Enter exhibition title"
                $hasError={!!errors.title}
              />
              {errors.title && (
                <ErrorMessage>{errors.title.message}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="description">Description</Label>
              <TextArea
                {...register('description')}
                id="description"
                placeholder="Describe this exhibition..."
                $hasError={!!errors.description}
              />
              {errors.description && (
                <ErrorMessage>{errors.description.message}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Tags *</Label>
              <TagSelector>
                {availableTags?.map((tag) => (
                  <TagOption key={tag.tag_id}>
                    <Checkbox
                      type="checkbox"
                      checked={selectedTags.includes(tag.tag_name)}
                      onChange={(e) => handleTagChange(tag.tag_name, e.target.checked)}
                    />
                    {tag.tag_name} ({tag.usage_count} artworks)
                  </TagOption>
                ))}
              </TagSelector>
              {errors.tags && (
                <ErrorMessage>{errors.tags.message}</ErrorMessage>
              )}
            </FormGroup>

            <ModalActions>
              <ModalButton type="button" $variant="secondary" onClick={closeModal}>
                Cancel
              </ModalButton>
              <ModalButton
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending 
                  ? 'Saving...' 
                  : editingExhibition ? 'Update Exhibition' : 'Create Exhibition'
                }
              </ModalButton>
            </ModalActions>
          </Form>
        </ExhibitionModalContent>
      </Modal>

      {/* Delete Confirmation */}
      {deleteExhibition && (
        <ConfirmDialog
          isOpen={!!deleteExhibition}
          title="Delete Exhibition"
          message={`Are you sure you want to delete "${deleteExhibition.title}"? This action cannot be undone.`}
          confirmText="Delete Exhibition"
          cancelText="Cancel"
          onConfirm={handleDelete}
          onCancel={() => setDeleteExhibition(null)}
          variant="danger"
          loading={deleteMutation.isPending}
        />
      )}

      {/* Featured Exhibition Limit Dialog */}
      <FeaturedExhibitionLimitDialog
        isOpen={isLimitDialogOpen}
        onClose={() => setIsLimitDialogOpen(false)}
        featuredExhibitions={featuredExhibitions}
        onSuccess={handleLimitDialogSuccess}
      />
    </>
  );
}
